import AppColors from 'general/constants/AppColors';
import imgFileArrowUp from 'assets/images/Dialogs/img_file-arrow-up.png';
import ChargeHelper from 'features/Charge/chargeHelper';
import miscHelper from 'general/helpers/miscHelper';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import KTFormInput, { KTFormInputType } from 'general/components/OtherKeenComponents/Forms/KTFormInput';
import PropTypes from 'prop-types';
import { useRef, useState } from 'react';

DialogUpdateFirmwareView.propTypes = {
  item: PropTypes.object.isRequired,
  onCancel: PropTypes.func,
  onDone: PropTypes.func,
  onNavigateDetail: PropTypes.func,
};

const SOURCE_TYPE = { LINK: 'link', FILE: 'file' };

function DialogUpdateFirmwareView({ item, onCancel, onDone, onNavigateDetail }) {
  const [sourceType, setSourceType] = useState(SOURCE_TYPE.LINK);
  const [firmwareUrl, setFirmwareUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (file) => {
    if (!file) return;
    setSelectedFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleSubmit = async () => {
    if (sourceType === SOURCE_TYPE.LINK) {
      if (!firmwareUrl.trim()) {
        ToastHelper.showError('Vui lòng nhập URL firmware');
        return;
      }
      setUploading(true);
      const result = await ChargeHelper.API.requestUpdateFirmware(item?.pointId, firmwareUrl);
      setUploading(false);
      if (result && typeof result === 'boolean') {
        setSuccess(true);
      } else {
        ToastHelper.showError(result || 'Cập nhật firmware thất bại');
      }
    } else {
      if (!selectedFile) {
        ToastHelper.showError('Vui lòng chọn tệp firmware');
        return;
      }
      setUploading(true);
      try {
        const mediaList = await miscHelper.callApiUploadFile(selectedFile);
        const filePath = mediaList?.[0]?.relativePath ?? mediaList?.[0];
        if (!filePath) throw new Error('Upload thất bại');
        const result = await ChargeHelper.API.requestUpdateFirmware(item?.pointId, filePath);
        setUploading(false);
        if (result && typeof result === 'boolean') {
          setSuccess(true);
        } else {
          ToastHelper.showError(result || 'Cập nhật firmware thất bại');
        }
      } catch (err) {
        setUploading(false);
        ToastHelper.showError('Tải lên tệp thất bại');
      }
    }
  };

  if (success) {
    return (
      <div>
        <div className="d-flex flex-column align-items-center justify-content-center text-center px-5 pt-6 pb-4 gap-3">
          <div
            style={{
              width: 80, height: 80, borderRadius: '50%',
              background: 'linear-gradient(135deg, #4ade80, #22c55e)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 0 0 16px rgba(34,197,94,0.12)',
            }}
          >
            <i className="fa-solid fa-check" style={{ fontSize: 36, color: 'white' }} />
          </div>
          <span style={UIHelper.buildFontStyle('600', '16px', AppColors.gray[800])}>
            Tải lên dữ liệu thành công
          </span>
          <span style={UIHelper.buildFontStyle('400', '14px', AppColors.gray[600])}>
            Dữ liệu update firmware đã được tải lên thành công. Vui lòng truy cập{' '}
            <span
              className="cursor-pointer"
              style={UIHelper.buildFontStyle('600', '14px', AppColors.primary)}
              onClick={() => { if (onNavigateDetail) onNavigateDetail(); }}
            >
              chi tiết trụ sạc
            </span>
            {' '}để theo dõi tiến trình.
          </span>
        </div>
        <div
          className="d-flex align-items-center justify-content-end gap-3 px-4 py-3"
          style={{ borderTop: `1px solid ${AppColors.gray[200]}` }}
        >
          {UIHelper.buildNegativeButton('Bỏ qua', () => { if (onDone) onDone(); })}
          {UIHelper.buildGreenButton('Chi tiết trụ sạc', () => {
            if (onNavigateDetail) onNavigateDetail();
            if (onDone) onDone();
          })}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* header */}
      <div className="d-flex align-items-center justify-content-between px-4 py-3" style={{ borderBottom: `1px solid ${AppColors.gray[200]}` }}>
        <span style={UIHelper.buildFontStyle('700', '20px', AppColors.gray[800])}>Cập nhật firmware</span>
        <button onClick={onCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: AppColors.gray[500] }}>
          <i className="fa-regular fa-xmark" />
        </button>
      </div>

      <div className="px-4 pt-4 pb-2">
        {/* description */}
        <p style={UIHelper.buildFontStyle('400', '13px', AppColors.gray[600])}>
          Chọn nguồn dữ liệu để tiến hành cập nhật
        </p>

        {/* radio source type - always visible */}
        <div
          className="d-flex gap-4"
          style={{
            borderBottom: sourceType === SOURCE_TYPE.LINK ? `1px dashed ${AppColors.gray[300]}` : 'none',
            paddingBottom: sourceType === SOURCE_TYPE.LINK ? 10 : 0,
            marginBottom: 16,
          }}
        >
          {[
            { value: SOURCE_TYPE.LINK, label: 'Link liên kết' },
            { value: SOURCE_TYPE.FILE, label: 'Tệp đính kèm' },
          ].map(({ value, label }) => (
            <div key={value} className="d-flex align-items-center cursor-pointer" style={{ gap: 8 }} onClick={() => setSourceType(value)}>
              <label className="radio radio-success mb-0" style={{ pointerEvents: 'none' }}>
                <input type="radio" name="firmwareSourceType" checked={sourceType === value} onChange={() => {}} />
                <span />
              </label>
              <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[700])}>{label}</span>
            </div>
          ))}
        </div>

        {/* link mode */}
        {sourceType === SOURCE_TYPE.LINK && (
          <div className="mb-3">
            {UIHelper.buildFormLabel('URL firmware', true)}
            <KTFormInput
              name="firmwareUrl"
              type={KTFormInputType.text}
              placeholder="Link liên kết (https://...)"
              value={firmwareUrl}
              onChange={(val) => setFirmwareUrl(val)}
            />
          </div>
        )}

        {/* file mode */}
        {sourceType === SOURCE_TYPE.FILE && (
          <>
            <div
              className="mb-3"
              style={{
                border: `1px dashed ${dragging ? AppColors.primary : '#30BD6F'}`,
                borderRadius: 8,
                background: 'rgba(48, 189, 111, 0.05)',
                transition: 'all 0.2s',
              }}
            >
              <div
                className="d-flex flex-column align-items-center justify-content-center cursor-pointer"
                style={{ padding: '32px 16px' }}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleDrop}
              >
                {uploading ? (
                  <>
                    <div className="spinner-border text-success mb-2" style={{ width: 32, height: 32 }} />
                    <span style={UIHelper.buildFontStyle('400', '13px', AppColors.gray[500])}>
                      Dữ liệu đang được tải lên
                    </span>
                  </>
                ) : (
                  <>
                    <img src={imgFileArrowUp} alt="" style={{ width: 48, height: 48, objectFit: 'contain', marginBottom: 8 }} />
                    <span style={UIHelper.buildFontStyle('500', '13px', AppColors.gray[700])}>
                      Kéo thả hoặc ấn để tải lên
                    </span>
                    <span style={UIHelper.buildFontStyle('400', '12px', AppColors.gray[400])}>
                      Định dạng: .bin, .img, .zip
                    </span>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".bin,.img,.zip"
                hidden
                onChange={(e) => handleFileSelect(e.target.files?.[0])}
              />
            </div>
            {selectedFile && (
              <div
                className="d-flex align-items-center justify-content-between mt-2 px-3 py-2"
                style={{ background: AppColors.gray[50], borderRadius: 6, border: `1px solid ${AppColors.gray[200]}` }}
              >
                <div className="d-flex align-items-center gap-2">
                  <i className="fa-solid fa-file-lines" style={{ color: AppColors.primary, fontSize: 16 }} />
                  <span style={UIHelper.buildFontStyle('400', '13px', AppColors.gray[700])}>
                    {selectedFile.name}
                  </span>
                </div>
                <button
                  onClick={() => setSelectedFile(null)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: AppColors.gray[400], fontSize: 14 }}
                >
                  <i className="fa-regular fa-xmark" />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <div
        className="d-flex align-items-center justify-content-end gap-3 px-4 py-3"
        style={{ borderTop: `1px solid ${AppColors.gray[200]}` }}
      >
        {UIHelper.buildNegativeButton('Hủy bỏ', onCancel)}
        {UIHelper.buildGreenButton('Tải lên', handleSubmit, null, false, uploading)}
      </div>
    </div>
  );
}

export default DialogUpdateFirmwareView;
